// BillingPage (C-BILL) — mirrors review/commerce-app/billing.jsx
//
// Ported from the 2026-08-26 design drop,
// handoff/commerce-app/src/pages/BillingPage.tsx. As in AppShell.tsx,
// design-system components are imported by module path rather than from the
// barrel, and button handlers are `onPress`.
//
// The one thing this page must never do is read as a bill. Every amount on it
// is composed here from what our ledger recorded and what our catalogue
// charges; the channel that invoices applies its own rounding, its own calendar,
// tax, and any credit on the account. So the payload marks every figure
// `estimate: true`, and this page says so three times over — in the disclaimer
// strip, on each summary card, and in the table's own caption (Owner D4). None
// of those is decoration: a customer who reads one number here as the amount
// owed has been misled by this screen.
//
// ADAPTED, and the departures worth reading before the code:
//
// * The status column. The handoff's fixtures carry "paid" / "invoiced" /
// "accruing" and an AWS invoice number. This service knows none of those: a
//   channel knows whether a customer paid, and never tells us. What we do know
//   is whether a period is still accruing and whether we could price the line at
//   all — so those are the two things the column says.
// * The last column and its "View in AWS" link are dropped, for the same
//   reason: there is no invoice reference in any record here to link to.
// * "Export history" is dropped. Nothing exports yet, and a button that does
//   nothing is worse than an absent one.
// * The year tabs FILTER. In the handoff they are decorative (`TabsContent` is
//   not even imported), which is defensible in a prototype and not on a page
//   with several years of real lines on it.
import { CreditCard, ExternalLink, Receipt } from "lucide-react";
import { Badge } from "@miq/design-system/components/ui/badge";
import { Button } from "@miq/design-system/components/ui/button";
import {
  Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle,
} from "@miq/design-system/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@miq/design-system/components/ui/table";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@miq/design-system/components/ui/tabs";
import { FailedToLoad, LoadingCard, LoadingTile, NothingYet } from "../components/DataStates";
import {
  channelConsole, channelName, invoicedBy, soleChannel, useBilling, usePacks,
  type BillingLine, type BillingPayload, type HeldPack, type PeriodTotal,
} from "../data/catalogue";
import { agreementShown } from "../data/presentation";
import { money, monthShort, periodSpan, quantity, rate, yearOf } from "../format";
import { useDocumentTitle } from "../../lib/documentTitle";

// RESTORED FROM review/commerce-app/billing.jsx. The handoff maps the
// prototype's tonal pills onto DS Badge variants, which do not carry the same
// vocabulary: "Accruing" went from amber to colourless and the billing-authority
// callout went from info-blue to neutral grey. Both losses matter on this page —
// amber is the signal that a period has not settled, and greying the disclaimer
// de-emphasises the one paragraph saying these figures are not a bill. The tone
// classes match the ones already used in AppShell.tsx and HomePage.tsx.
//
// Exported, because the staff Fleet Revenue screen shows the same estimates to
// a colleague and wears the same two tones for them: the disclosure strip in
// the same words, and the pending state in the colour that says "not settled".
export const ACCRUING_TONE = "border-warning/30 bg-warning-soft text-warning";
export const DISCLOSURE_TONE = "border-info/30 bg-info-soft text-info";

export function BillingPage() {
  useDocumentTitle('Billing');
  const billing = useBilling();
  const packs = usePacks();
  // WAS HARDCODED TO AWS: this page named AWS Marketplace as the invoicing
  // authority and linked a fixed AWS console, while its own lines carry a
  // channel each and Home already derived the wording from them. A customer
  // billed through another channel read somebody else's invoicer's name over
  // their own figures. Derived here, once, and null where the lines disagree
  // or the channel has no console this service knows — in which case the
  // wording falls back to the channel-free sentence and the button is not
  // drawn, rather than sending somebody to a console they have no account in.
  const channel = billing.status === "ready" ? soleChannel(billing.data.lines) : null;
  const channelLabel = channelName(channel);
  const consoleUrl = channelConsole(channel);

  return (
    <div className="space-y-5 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Commerce · Billing</div>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">Billing</h1>
          <p className="mt-1 text-sm text-muted-foreground">Metered usage priced against your agreements, across all instances and decision packs. Charges are {invoicedBy(channel)}.</p>
        </div>
        {consoleUrl && channelLabel && (
          <Button variant="outline" onPress={() => window.open(consoleUrl, "_blank", "noopener")}>
            Open {channelLabel} <ExternalLink className="size-3 opacity-70" />
          </Button>
        )}
      </div>

      {/* The disclosure, first and unmissable. It is the difference between a
          page of estimates and a page of charges, and the handoff put it here
          for that reason. */}
      <div className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${DISCLOSURE_TONE}`}>
        <CreditCard className="mt-0.5 size-4 shrink-0" />
        <p className="text-xs leading-relaxed">
          <span className="font-semibold">Every amount below is an estimate.</span>{" "}
          mDecide does not charge your account directly. The quantities are what our metering ledger recorded and handed to {channelLabel ?? "the sales channel"}, priced at your agreement's rates — {channelLabel ?? "the channel that sold each subscription"} is the authority for what you are invoiced, and applies its own rounding, billing calendar, and tax.{" "}
          {consoleUrl && channelLabel && (
            <a className="font-medium underline" href={consoleUrl} target="_blank" rel="noreferrer">{channelLabel} subscriptions</a>
          )}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {billing.status === "loading" && <><LoadingTile /><LoadingTile /><LoadingTile /></>}
        {billing.status === "failed" && (
          <div className="col-span-full"><FailedToLoad what="Your billing summary" error={billing.error} /></div>
        )}
        {billing.status === "ready" && (
          <>
            <SummaryCard
              label="Current period estimate"
              total={billing.data.current_period}
              sub={periodSpan(billing.data.current_period_start)}
              badge={<Badge variant="outline" className={ACCRUING_TONE}>Accruing</Badge>}
            />
            <SummaryCard
              label="Previous period"
              total={billing.data.previous_period}
              sub={`${periodSpan(billing.data.previous_period_start)} · ${invoicedBy(channel)}`}
            />
            <SummaryCard
              label="Estimated year to date"
              total={billing.data.year_to_date}
              sub="All decision packs, this calendar year"
              icon={<Receipt className="size-3.5 text-muted-foreground" />}
            />
          </>
        )}
      </div>

      {billing.status === "loading" && <LoadingCard lines={6} />}
      {billing.status === "ready" && <History data={billing.data} />}

      <div>
        <h2 className="text-lg font-semibold tracking-tight">Decision packs</h2>
        <p className="mt-1 text-sm text-muted-foreground">What each pack charges for, and the rate on your agreement.</p>
        {packs.status === "loading" && <div className="mt-4"><LoadingCard lines={5} /></div>}
        {packs.status === "failed" && <div className="mt-4"><FailedToLoad what="Your decision packs" error={packs.error} /></div>}
        {packs.status === "ready" && <PackCards packs={packs.data.packs} />}
      </div>
    </div>
  );
}

/**
 * One summary figure and its estimate mark.
 *
 * ADAPTED: the handoff's three cards carry a status badge each ("Accruing",
 * "Paid"). "Paid" is a claim about a payment this service never sees, so the
 * badge here is the estimate mark, which is a claim we can stand behind.
 */
function SummaryCard({ label, total, sub, badge, icon }: {
  label: string;
  total: PeriodTotal;
  sub: string;
  badge?: React.ReactNode;
  icon?: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="pt-5">
        <div className="flex items-center justify-between text-xs font-medium text-muted-foreground">
          {label}
          {icon}
        </div>
        <div className="mt-1.5 flex items-baseline gap-2">
          <span className="text-2xl font-semibold tracking-tight">{money(total.amount, total.currency)}</span>
          <Badge variant="outline">Estimate</Badge>
          {badge}
        </div>
        <div className="mt-1 text-xs text-muted-foreground">{sub}</div>
        {total.mixed_currencies && (
          <div className="mt-1 text-xs text-warning">
            Lines in more than one currency — see the table below; no combined total is shown.
          </div>
        )}
        {total.unpriced_lines > 0 && (
          <div className="mt-1 text-xs text-warning">
            {total.unpriced_lines} line{total.unpriced_lines === 1 ? "" : "s"} could not be priced and are excluded.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * The history table, one card, with the year tabs actually filtering it.
 *
 * The tab set is derived from the years the lines cover rather than hardcoded,
 * so a customer in their first month sees one tab and not a "2025" that holds
 * nothing.
 */
function History({ data }: { data: BillingPayload }) {
  const years = [...new Set(data.lines.map((line) => yearOf(line.period_start)))].sort().reverse();

  return (
    <Tabs defaultValue={years[0] ?? "none"}>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Billing history</CardTitle>
          <CardDescription>
            One line per pack dimension per period · usage metered hourly,
            settled monthly · every amount an estimate
          </CardDescription>
          {years.length > 0 && (
            <CardAction>
              <TabsList>
                {years.map((year) => (
                  <TabsTrigger key={year} value={year}>{year}</TabsTrigger>
                ))}
              </TabsList>
            </CardAction>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {years.length === 0 && (
            <NothingYet>
              No usage has been metered and settled yet. Lines appear here
              once the sales channel has answered for the first hour.
            </NothingYet>
          )}
          {years.map((year) => (
            <TabsContent key={year} value={year}>
              <LineTable
                lines={data.lines.filter((line) => yearOf(line.period_start) === year)}
                currentPeriodStart={data.current_period_start}
              />
            </TabsContent>
          ))}
        </CardContent>
      </Card>
    </Tabs>
  );
}

function LineTable({ lines, currentPeriodStart }: { lines: BillingLine[]; currentPeriodStart: string }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Period</TableHead>
          <TableHead>Decision pack</TableHead>
          <TableHead>Dimension</TableHead>
          <TableHead className="text-right">Quantity</TableHead>
          <TableHead className="text-right">Rate / unit</TableHead>
          <TableHead className="text-right">Amount (est.)</TableHead>
          <TableHead className="text-right">Hours</TableHead>
          <TableHead>Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {lines.map((line) => (
          // A period, an agreement and a dimension are what make a line, so
          // together they are its identity — the handoff keys on the array
          // index, which reorders wrongly the moment the table is filtered.
          <TableRow key={`${line.period_start}-${line.agreement_ref}-${line.dimension}`}>
            <TableCell className="font-medium">{monthShort(line.period_start)}</TableCell>
            <TableCell>{line.pack_name ?? <span className="text-muted-foreground">Not in catalogue</span>}</TableCell>
            <TableCell className="font-mono text-xs text-muted-foreground">{line.dimension}</TableCell>
            <TableCell className="text-right">
              {quantity(line.quantity)}
              {line.unit_short && <span className="ml-1 text-xs text-muted-foreground">{line.unit_short}</span>}
            </TableCell>
            <TableCell className="text-right">{rate(line.rate, line.currency)}</TableCell>
            <TableCell className="text-right font-medium">{money(line.amount, line.currency)}</TableCell>
            <TableCell className="text-right text-xs text-muted-foreground">{line.hours}</TableCell>
            <TableCell><LineStatus line={line} currentPeriodStart={currentPeriodStart} /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/**
 * What this service can honestly say about a line's standing.
 *
 * Three answers, and none of them is "paid". A line we could not price is the
 * one a reader must not skim past, so it is the one that stands out.
 */
function LineStatus({ line, currentPeriodStart }: { line: BillingLine; currentPeriodStart: string }) {
  if (!line.priced) {
    return <Badge variant="destructive">Not priced</Badge>;
  }
  if (line.period_start === currentPeriodStart) {
    return <Badge variant="outline" className={ACCRUING_TONE}>Accruing</Badge>;
  }
  return <Badge variant="secondary">Submitted</Badge>;
}

/**
 * A card per pack: the terms, the dimensions, and the rate on each.
 *
 * ADAPTED: the handoff filters to `status === "active"` and would render a
 * pending pack with two empty rows. Here a pending agreement has no pack at all,
 * so it gets a card of its own saying what it is waiting for — the customer's
 * newest agreement being the one thing worth not hiding.
 */
function PackCards({ packs }: { packs: HeldPack[] }) {
  if (packs.length === 0) {
    return <NothingYet>No agreements on this account yet.</NothingYet>;
  }

  return (
    <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
      {packs.map((entry) => (
        <Card key={entry.agreement_ref}>
          <CardHeader>
            <CardTitle className="text-base">{entry.pack?.name ?? "Awaiting setup"}</CardTitle>
            <CardDescription>
              {entry.pack ? `${entry.pack.pack_number} · ${entry.pack.description}` : "Decision pack assigned at setup"}
            </CardDescription>
            <div className="mt-1 font-mono text-xs text-muted-foreground">{entry.agreement_ref}</div>
            <CardAction>
              {/* The state as the ledger records it, in the reader's words —
                  through the one rule every surface shares, so an internal
                  agreement reads as not billable rather than as waiting. */}
              <Badge variant={agreementShown(entry).variant}>{agreementShown(entry).label}</Badge>
            </CardAction>
          </CardHeader>
          <CardContent>
            <dl className="space-y-2 text-sm">
              <Row label="Pricing" value={entry.pack?.pricing ?? "Set at activation"} />
              <Row
                label="Dimension"
                value={
                  entry.pack?.dimensions.length
                    ? <span className="font-mono text-xs">{entry.pack.dimensions.map((d) => d.key).join(", ")}</span>
                    : <span className="text-muted-foreground">None yet</span>
                }
              />
              <Row
                label="Rate"
                value={
                  entry.pack?.dimensions.length
                    ? entry.pack.dimensions.map((d) => `${rate(d.rate, d.currency)} per ${d.unit_short}`).join(" · ")
                    : <span className="text-muted-foreground">None yet</span>
                }
              />
              <Row label="Term" value={entry.pack?.term ?? "Starts on activation"} />
              <Row label="Instances" value={`${entry.instance_count} (${entry.running_instance_count} running)`} />
            </dl>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right">{value}</dd>
    </div>
  );
}
